import React, { useState, useEffect, useMemo } from 'react';
import { useProceso } from '../../hooks/useProceso';
import { useProrrateo, claveSI } from '../../hooks/useProrrateo';
import { MONTHS } from '../../constants';
import { generateProcesoExcel, generateReporteGeneral } from '../../utils/exporters';
import { tbbcAPI, optimizeAPI, axdAPI } from '../../services/api';
import { formatCurrency } from '../../utils/formatters';
import { ConfirmModal } from '../common/ConfirmModal';
import { ServicioAsignarModal } from '../common/ServicioAsignarModal';
import { ProrrateoMatriz } from './ProrrateoMatriz';

/**
 * Módulo Cloud (rediseño 2026-05). Distribuye los costos de IBM Cloud
 * (importados por CSV) entre los proyectos/clientes.
 * Columna 1: clientes (reordenables, zona de drop del prorrateo).
 * Columna 2: costo agrupado por servicio·instancia (arrastrable a un cliente).
 * Columna 3: carga del CSV y tabla de datos.
 * Abajo: la matriz de prorrateo.
 */
export const ProcesoModule = ({
  showNotification,
  // Estado de sesión Cloud elevado a App.jsx para que sobreviva al cambio de
  // pestaña (App permanece montado; este módulo se desmonta al cambiar de tab).
  fileUpload,
  selectedMonth,
  setSelectedMonth,
  trm,
  setTrm
}) => {
  const {
    clientes,
    loadClientes,
    reorderClientes,
    addCliente,
    removeCliente
  } = useProceso(showNotification);

  const [nuevoCliente, setNuevoCliente] = useState('');
  const [confirm, setConfirm] = useState(null);
  // Modal de asignación: { servicios: [...], bulk: bool } o null
  const [modalState, setModalState] = useState(null);
  // Selección múltiple en la columna 2 (claves servicio|||instancia)
  const [seleccionados, setSeleccionados] = useState(() => new Set());
  // Búsqueda de servicio en la columna 2
  const [busqueda, setBusqueda] = useState('');
  const [generandoReporte, setGenerandoReporte] = useState(false);
  // Drag & drop: { type: 'client' | 'servicio', ... } o null
  const [dragInfo, setDragInfo] = useState(null);
  const [dragOverClientId, setDragOverClientId] = useState(null);
  // Columna 3: carga de CSV (estado provisto por App.jsx vía props)
  const { data, columns, loading, handleFileChange, uploadFile } = fileUpload;
  const { prorrateo, loadProrrateo, setCelda, distribuirEquitativo } = useProrrateo(showNotification);

  useEffect(() => {
    loadClientes();
    loadProrrateo();
  }, [loadClientes, loadProrrateo]);

  const handleCsvSelect = (selectedFile) => {
    if (!selectedFile) return;
    handleFileChange(selectedFile);
    uploadFile(selectedFile);
  };

  const handleExportExcel = () => {
    generateProcesoExcel({
      data,
      columns,
      selectedMonth,
      onSuccess: (title, msg) => showNotification?.('success', title, msg),
      onError: (title, msg) => showNotification?.('error', title, msg)
    });
  };

  // Reporte general: combina Cloud (este módulo) con TBBC, Optimize y AXD.
  // Los costos de Cloud están en USD y se convierten a COP con la TRM.
  const handleReporteGeneral = async () => {
    const trmNum = parseFloat(trm) || 0;
    if (trmNum <= 0) {
      showNotification?.(
        'error',
        'TRM requerida',
        'Ingresa una TRM válida para convertir los costos de Cloud a COP'
      );
      return;
    }
    setGenerandoReporte(true);
    try {
      // Carga apps + asignaciones + proyectos de un módulo de administración
      const fetchModulo = async (apiObj) => {
        const [appsRes, asigRes, proyRes] = await Promise.all([
          apiObj.getApps(),
          apiObj.getAsignaciones(),
          apiObj.getProyectos()
        ]);
        const apps = (appsRes.data.apps || []).map((a) => ({
          ...a,
          costo: parseFloat(a.costo) || 0
        }));
        const asig = {};
        for (const r of asigRes.data.prorrateo || []) {
          if (!asig[r.app_id]) asig[r.app_id] = {};
          asig[r.app_id][r.client_id] = true;
        }
        return { apps, asig, proyectos: proyRes.data.proyectos || [] };
      };

      const [tbbc, optimize, axd] = await Promise.all([
        fetchModulo(tbbcAPI),
        fetchModulo(optimizeAPI),
        fetchModulo(axdAPI)
      ]);

      // Acumulador por cliente: id -> { name, cloud(USD), tbbc, optimize, axd }
      const acc = {};
      const ensure = (id, name) => {
        const key = Number(id);
        if (!acc[key]) {
          acc[key] = { name: name || '', cloud: 0, tbbc: 0, optimize: 0, axd: 0 };
        } else if (name && !acc[key].name) {
          acc[key].name = name;
        }
        return acc[key];
      };

      // Cloud: costo (USD) repartido por el % de prorrateo de cada servicio
      clientes.forEach((c) => ensure(c.id, c.name));
      agrupado.forEach((g) => {
        const dist = prorrateo[claveSI(g.service, g.instance)] || {};
        Object.entries(dist).forEach(([cid, pct]) => {
          if (pct > 0) ensure(cid).cloud += g.total * (pct / 100);
        });
      });

      // TBBC / Optimize / AXD: costo de cada herramienta por proyecto.
      // Respeta el modo por herramienta (igual que la matriz):
      //  - 'unitario': cada proyecto asignado paga el valor completo
      //  - 'dividido' (default): el valor se reparte entre los proyectos asignados
      const aplicarModulo = (mod, campo) => {
        const validIds = new Set(mod.proyectos.map((p) => Number(p.id)));
        mod.proyectos.forEach((p) => ensure(p.id, p.name));
        mod.apps.forEach((a) => {
          const ids = Object.keys(mod.asig[a.id] || {})
            .map(Number)
            .filter((id) => validIds.has(id));
          if (ids.length === 0) return;
          const monto = a.modo === 'unitario' ? a.costo : a.costo / ids.length;
          ids.forEach((cid) => {
            ensure(cid)[campo] += monto;
          });
        });
      };
      aplicarModulo(tbbc, 'tbbc');
      aplicarModulo(optimize, 'optimize');
      aplicarModulo(axd, 'axd');

      const filas = Object.entries(acc).map(([id, v]) => ({
        name: v.name || `Cliente #${id}`,
        cloud: v.cloud,
        tbbc: v.tbbc,
        optimize: v.optimize,
        axd: v.axd
      }));

      generateReporteGeneral({
        filas,
        trm: trmNum,
        selectedMonth,
        onSuccess: (t, m) => showNotification?.('success', t, m),
        onError: (t, m) => showNotification?.('error', t, m)
      });
    } catch (error) {
      console.error('Error generando el reporte general:', error);
      showNotification?.(
        'error',
        'Error',
        'No se pudieron cargar los datos de TBBC, Optimize o AXD'
      );
    } finally {
      setGenerandoReporte(false);
    }
  };

  // Métricas del CSV cargado
  const totalCost = data.reduce((sum, row) => sum + (parseFloat(row['Cost']) || 0), 0);
  const serviciosUnicos = new Set(data.map((row) => row['Service Name'])).size;

  // Costo agrupado por servicio + instancia (una línea por combinación)
  const agrupado = useMemo(() => {
    const map = new Map();
    for (const row of data) {
      const service = row['Service Name'] || '';
      const instance = row['Instance Name'] || '';
      const key = `${service}|||${instance}`;
      const cost = parseFloat(row['Cost']) || 0;
      if (map.has(key)) {
        map.get(key).total += cost;
      } else {
        map.set(key, { service, instance, total: cost });
      }
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [data]);

  // Filtro de búsqueda de la columna 2 (por servicio o instancia)
  const agrupadoFiltrado = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return agrupado;
    return agrupado.filter(
      (g) =>
        g.service.toLowerCase().includes(q) ||
        g.instance.toLowerCase().includes(q)
    );
  }, [agrupado, busqueda]);

  const totalFiltrado = agrupadoFiltrado.reduce((s, g) => s + g.total, 0);

  const handleAddCliente = async () => {
    if (!nuevoCliente.trim()) return;
    if (await addCliente(nuevoCliente.trim())) setNuevoCliente('');
  };

  // --- Drag & drop ---
  const handleClientDragStart = (e, c) => {
    setDragInfo({ type: 'client', id: c.id });
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', `client:${c.id}`);
  };

  const handleServicioDragStart = (e, g) => {
    setDragInfo({ type: 'servicio', service: g.service, instance: g.instance });
    e.dataTransfer.effectAllowed = 'copy';
    e.dataTransfer.setData('text/plain', `servicio:${g.service}`);
  };

  const handleDragEnd = () => {
    setDragInfo(null);
    setDragOverClientId(null);
  };

  const handleClientDragOver = (e, c) => {
    if (!dragInfo) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = dragInfo.type === 'client' ? 'move' : 'copy';
    if (dragOverClientId !== c.id) setDragOverClientId(c.id);
  };

  const handleClientDragLeave = (c) => {
    if (dragOverClientId === c.id) setDragOverClientId(null);
  };

  const handleClientDrop = (e, c) => {
    e.preventDefault();
    const info = dragInfo;
    setDragOverClientId(null);
    setDragInfo(null);
    if (!info) return;

    if (info.type === 'client') {
      if (info.id === c.id) return;
      const list = [...clientes];
      const from = list.findIndex((x) => x.id === info.id);
      const to = list.findIndex((x) => x.id === c.id);
      if (from === -1 || to === -1) return;
      const [moved] = list.splice(from, 1);
      list.splice(to, 0, moved);
      reorderClientes(list);
    } else if (info.type === 'servicio') {
      const k = claveSI(info.service, info.instance);
      const distActual = prorrateo[k] || {};
      const idsActuales = Object.keys(distActual)
        .filter((id) => distActual[id] > 0)
        .map(Number);
      const nuevos = idsActuales.includes(c.id)
        ? idsActuales
        : [...idsActuales, c.id];
      distribuirEquitativo(info.service, info.instance, nuevos);
      showNotification?.(
        'success',
        'Prorrateo',
        `"${info.service}" repartido entre ${nuevos.length} cliente${nuevos.length === 1 ? '' : 's'}`
      );
    }
  };

  // --- Selección múltiple de servicios (columna 2) ---
  const toggleSeleccion = (g) => {
    const k = claveSI(g.service, g.instance);
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (next.has(k)) next.delete(k);
      else next.add(k);
      return next;
    });
  };

  // El "seleccionar todos" actúa sobre los servicios visibles (tras el filtro)
  const clavesFiltradas = agrupadoFiltrado.map((g) => claveSI(g.service, g.instance));
  const todosServiciosSel =
    clavesFiltradas.length > 0 && clavesFiltradas.every((k) => seleccionados.has(k));

  const toggleSeleccionTodos = () => {
    setSeleccionados((prev) => {
      const next = new Set(prev);
      if (todosServiciosSel) clavesFiltradas.forEach((k) => next.delete(k));
      else clavesFiltradas.forEach((k) => next.add(k));
      return next;
    });
  };

  const abrirModalServicio = (g) => setModalState({ servicios: [g], bulk: false });

  const abrirModalSeleccion = () => {
    const servicios = agrupado.filter((g) =>
      seleccionados.has(claveSI(g.service, g.instance))
    );
    if (servicios.length > 0) setModalState({ servicios, bulk: true });
  };

  // Asignación vía modal: reparte el/los servicio(s) entre los clientes elegidos
  const handleAsignarServicio = (clientIds) => {
    if (!modalState || modalState.servicios.length === 0) return;
    const { servicios, bulk } = modalState;
    servicios.forEach((s) => distribuirEquitativo(s.service, s.instance, clientIds));
    const ns = servicios.length;
    const etiquetaServicios = ns === 1 ? `"${servicios[0].service}"` : `${ns} servicios`;
    if (clientIds.length === 0) {
      showNotification?.('info', 'Prorrateo', `${etiquetaServicios} sin asignar`);
    } else {
      showNotification?.(
        'success',
        'Prorrateo',
        `${etiquetaServicios} repartido${ns === 1 ? '' : 's'} entre ${clientIds.length} cliente${clientIds.length === 1 ? '' : 's'}`
      );
    }
    setModalState(null);
    if (bulk) setSeleccionados(new Set());
  };

  const pedirEliminarCliente = (e, cliente) => {
    e.stopPropagation();
    setConfirm({
      title: 'Eliminar cliente',
      message: `¿Eliminar el cliente "${cliente.name}"? Se quitará de los prorrateos.`,
      onConfirm: async () => {
        await removeCliente(cliente);
        setConfirm(null);
      }
    });
  };

  return (
    <div className="module-proceso">
      <div className="module-header">
        <h1>Cloud</h1>
        <p>Distribución de costos de IBM Cloud entre clientes y proyectos</p>
      </div>

      {/* Barra superior — carga del CSV, métricas y reporte general */}
      <div className="proceso-toolbar">
        <div className="proceso-toolbar-group">
          <select
            className="proceso-input proceso-toolbar-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          >
            <option value="">Seleccionar mes…</option>
            {MONTHS.map((m) => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>

          <input
            type="file"
            id="proceso-csv-upload"
            hidden
            accept=".csv"
            onChange={(e) => handleCsvSelect(e.target.files[0])}
            disabled={loading}
          />
          <label
            htmlFor="proceso-csv-upload"
            className={`proceso-upload-btn ${loading ? 'disabled' : ''}`}
          >
            {loading ? 'Procesando…' : 'Importar CSV'}
          </label>

          <button
            className="proceso-export-btn"
            onClick={handleExportExcel}
            disabled={data.length === 0}
          >
            Exportar a Excel
          </button>
        </div>

        {data.length > 0 ? (
          <div className="proceso-toolbar-stats">
            <div className="proceso-tb-stat proceso-tb-stat-main">
              <span className="proceso-tb-stat-label">
                Costo total{selectedMonth ? ` · ${selectedMonth}` : ''}
              </span>
              <span className="proceso-tb-stat-value">{formatCurrency(totalCost)}</span>
            </div>
            <div className="proceso-tb-stat">
              <span className="proceso-tb-stat-label">Filas</span>
              <span className="proceso-tb-stat-num">{data.length}</span>
            </div>
            <div className="proceso-tb-stat">
              <span className="proceso-tb-stat-label">Servicios</span>
              <span className="proceso-tb-stat-num">{serviciosUnicos}</span>
            </div>
          </div>
        ) : (
          <span className="proceso-toolbar-hint">
            Importa un CSV de facturación para empezar a distribuir los costos.
          </span>
        )}

        <div className="proceso-toolbar-report">
          <input
            className="proceso-input proceso-tb-trm"
            type="number"
            min="0"
            step="0.01"
            value={trm}
            onChange={(e) => setTrm(e.target.value)}
            placeholder="TRM USD→COP"
            disabled={data.length === 0}
          />
          <button
            className="proceso-reporte-btn"
            onClick={handleReporteGeneral}
            disabled={data.length === 0 || generandoReporte}
            title="Reporte general: Cloud + TBBC + Optimize + AXD"
          >
            {generandoReporte ? 'Generando…' : 'Generar reporte general'}
          </button>
        </div>
      </div>

      <div className="proceso-grid">
        {/* Columna 1 — Clientes */}
        <section className="proceso-col">
          <header className="proceso-col-header">
            <span className="proceso-col-num">1</span>
            <span className="proceso-col-title">Clientes</span>
            <span className="proceso-col-count">{clientes.length}</span>
          </header>
          <div className="proceso-col-body">
            <div className="proceso-form">
              <input
                className="proceso-input"
                value={nuevoCliente}
                onChange={(e) => setNuevoCliente(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddCliente()}
                placeholder="Nombre del cliente"
              />
              <button className="btn-primary proceso-add-btn" onClick={handleAddCliente}>
                Agregar
              </button>
            </div>

            <ul className="proceso-list">
              {clientes.length === 0 && (
                <li className="proceso-list-empty">Sin clientes todavía</li>
              )}
              {clientes.map((c) => {
                const isDragging = dragInfo?.type === 'client' && dragInfo.id === c.id;
                let dragOverClass = '';
                if (dragOverClientId === c.id && dragInfo) {
                  dragOverClass = dragInfo.type === 'client' ? 'drag-over-reorder' : 'drag-over-app';
                }
                return (
                  <li
                    key={c.id}
                    className={`proceso-list-item ${isDragging ? 'dragging' : ''} ${dragOverClass}`}
                    draggable
                    onDragStart={(e) => handleClientDragStart(e, c)}
                    onDragEnd={handleDragEnd}
                    onDragOver={(e) => handleClientDragOver(e, c)}
                    onDragLeave={() => handleClientDragLeave(c)}
                    onDrop={(e) => handleClientDrop(e, c)}
                  >
                    <span className="proceso-list-name">{c.name}</span>
                    <button
                      className="proceso-del-btn"
                      onClick={(e) => pedirEliminarCliente(e, c)}
                      title="Eliminar cliente"
                    >
                      ×
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>

        {/* Columna 2 — Costo agrupado por servicio + instancia (arrastrable) */}
        <section className="proceso-col">
          <header className="proceso-col-header">
            <span className="proceso-col-num">2</span>
            <span className="proceso-col-title">Servicio · Instancia</span>
            <span className="proceso-col-count">{agrupado.length}</span>
          </header>
          <div className="proceso-col-body">
            {agrupado.length > 0 ? (
              <>
                <div className="proceso-search">
                  <svg
                    className="proceso-search-icon"
                    viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  >
                    <circle cx="11" cy="11" r="8" />
                    <path d="m21 21-4.3-4.3" />
                  </svg>
                  <input
                    className="proceso-search-input"
                    value={busqueda}
                    onChange={(e) => setBusqueda(e.target.value)}
                    placeholder="Buscar servicio o instancia…"
                  />
                  {busqueda && (
                    <button
                      className="proceso-search-clear"
                      onClick={() => setBusqueda('')}
                      title="Limpiar búsqueda"
                    >
                      ×
                    </button>
                  )}
                </div>

                {seleccionados.size > 0 ? (
                  <div className="proceso-bulk-bar">
                    <span className="proceso-bulk-count">
                      {seleccionados.size} servicio{seleccionados.size === 1 ? '' : 's'} seleccionado{seleccionados.size === 1 ? '' : 's'}
                    </span>
                    <button className="proceso-bulk-btn" onClick={abrirModalSeleccion}>
                      Asignar a clientes
                    </button>
                    <button
                      className="proceso-bulk-clear"
                      onClick={() => setSeleccionados(new Set())}
                    >
                      Limpiar
                    </button>
                  </div>
                ) : (
                  <p className="proceso-context">
                    {busqueda
                      ? `${agrupadoFiltrado.length} resultado${agrupadoFiltrado.length === 1 ? '' : 's'} de ${agrupado.length}`
                      : `${agrupado.length} servicio${agrupado.length === 1 ? '' : 's'} · arrástralos, haz clic o marca varios`}
                  </p>
                )}
                <div className="proceso-table-wrap">
                  <table className="proceso-table proceso-table-check">
                    <thead>
                      <tr>
                        <th className="proceso-check-col">
                          <input
                            type="checkbox"
                            checked={todosServiciosSel}
                            onChange={toggleSeleccionTodos}
                            title="Seleccionar todos"
                          />
                        </th>
                        <th>Service Name</th>
                        <th>Instance Name</th>
                        <th>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {agrupadoFiltrado.length === 0 && (
                        <tr>
                          <td colSpan={4} className="proceso-tabla-vacia">
                            Sin resultados para “{busqueda}”
                          </td>
                        </tr>
                      )}
                      {agrupadoFiltrado.map((g, i) => {
                        const k = claveSI(g.service, g.instance);
                        const dist = prorrateo[k] || {};
                        const suma =
                          Math.round(
                            Object.values(dist).reduce((s, v) => s + v, 0) * 100
                          ) / 100;
                        const estado =
                          suma >= 100 ? 'distribuido' : suma > 0 ? 'parcial' : 'pendiente';
                        const estadoTexto =
                          estado === 'distribuido'
                            ? 'Distribuido'
                            : estado === 'parcial'
                              ? `Parcial (${suma}%)`
                              : 'Sin distribuir';
                        const marcado = seleccionados.has(k);
                        return (
                          <tr
                            key={i}
                            className={`proceso-fila-drag ${marcado ? 'fila-marcada' : ''}`}
                            draggable
                            onDragStart={(e) => handleServicioDragStart(e, g)}
                            onDragEnd={handleDragEnd}
                            onClick={() => abrirModalServicio(g)}
                            title="Clic para asignar a clientes"
                          >
                            <td
                              className="proceso-check-col"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={marcado}
                                onChange={() => toggleSeleccion(g)}
                              />
                            </td>
                            <td>
                              <span
                                className={`estado-dot estado-${estado}`}
                                title={estadoTexto}
                              />
                              {g.service}
                            </td>
                            <td>{g.instance}</td>
                            <td>{formatCurrency(g.total)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={3}>Total</td>
                        <td>{formatCurrency(totalFiltrado)}</td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            ) : (
              <p className="proceso-hint">
                Importa un CSV en la barra superior para ver el costo agrupado.
              </p>
            )}
          </div>
        </section>

      </div>

      <ProrrateoMatriz
        clientes={clientes}
        agrupado={agrupado}
        prorrateo={prorrateo}
        onSetCelda={setCelda}
        selectedMonth={selectedMonth}
        showNotification={showNotification}
      />

      {confirm && (
        <ConfirmModal
          title={confirm.title}
          message={confirm.message}
          onConfirm={confirm.onConfirm}
          onClose={() => setConfirm(null)}
        />
      )}

      {modalState && (
        <ServicioAsignarModal
          servicios={modalState.servicios}
          clientes={clientes}
          asignados={
            modalState.servicios.length === 1
              ? Object.entries(
                  prorrateo[
                    claveSI(
                      modalState.servicios[0].service,
                      modalState.servicios[0].instance
                    )
                  ] || {}
                )
                  .filter(([, v]) => v > 0)
                  .map(([id]) => Number(id))
              : []
          }
          onConfirm={handleAsignarServicio}
          onClose={() => setModalState(null)}
        />
      )}
    </div>
  );
};
